import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useDeliveryAddressContext } from '../contexts/DeliveryAddressContext';
import GeocodingService from '../services/GeocodingService';
import { deliveryServiceabilityService } from '../services/DeliveryServiceabilityService';
import { vendorAddressTracker } from '../services/VendorAddressTracker';
import { logger } from '../utils/logger';
import { isStoreCurrentlyOpen } from '../utils/storeHours';

// Load test utilities in development mode
if (__DEV__) {
  require('../utils/VendorAddressTestUtils');
}

interface UseProductsOptions {
  searchQuery?: string;
  category?: string;
  subcategory?: string;
  item?: string;
  sortBy?: string;
  filters?: {
    condition?: string;
    size?: string;
    priceRange?: string;
    rating?: string;
    distance?: string;
    main_category?: string;
    category_subcategory?: string;
    category_item?: string;
    availability?: 'open' | 'closed';
  };
  featured?: boolean;
  limit?: number;
}

// Function to calculate distance between two points
function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 3958.8; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c; // Distance in miles
}

export function useProducts(options: UseProductsOptions = {}) {
  const queryClient = useQueryClient();
  const { 
    searchQuery, 
    category, 
    subcategory, 
    item, 
    filters = {},
    sortBy = 'newest',
    featured,
    limit
  } = options;
  
  const { selectedAddress } = useDeliveryAddressContext();

  useEffect(() => {
    const channel = supabase
      .channel('products-public-feed')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'products'
        },
        (payload) => {
          const newRow: any = payload.new;
          const oldRow: any = payload.old;

          const quantityChanged = Number(newRow?.quantity ?? NaN) !== Number(oldRow?.quantity ?? NaN);
          const statusChanged = newRow?.status !== oldRow?.status;

          if (!quantityChanged && !statusChanged) return;

          if (Number(newRow?.quantity ?? 0) <= 0 || newRow?.status !== oldRow?.status) {
            queryClient.invalidateQueries({ queryKey: ['products'] }).catch((err) => {
              logger.warn('useProducts', 'Failed to invalidate cached product lists', err);
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  return useQuery({
    queryKey: ['products', { 
      searchQuery, 
      category, 
      subcategory, 
      item, 
      filters, 
      sortBy, 
      featured, 
      limit,
      selectedAddress: selectedAddress ? {
        latitude: selectedAddress.latitude,
        longitude: selectedAddress.longitude
      } : null
    }],
    enabled: true,
    queryFn: async () => {
      try {
        let query = supabase
          .from('products')
          .select(`
            *,
            vendor:vendors (
              id,
              store_name,
              description,
              borough,
              business_type,
              status,
              rating,
              latitude,
              longitude,
              delivery_radius,
              street_line1,
              city,
              state,
              zip_code,
              country,
              delivery_hours
            )
          `);

        if (searchQuery) {
          // Create the search term with wildcards for partial matching
          const searchTerm = searchQuery.toLowerCase();
          
          // Reset to the base query and apply a single OR filter for all fields
          // This is the compatible way to do OR conditions in Supabase
          query = query.or(
            `title.ilike.%${searchTerm}%,` +
            `category_main.ilike.%${searchTerm}%,` +
            `category_subcategory.ilike.%${searchTerm}%,` +
            `category_item.ilike.%${searchTerm}%,` +
            `brand.ilike.%${searchTerm}%`
          );
        }

        // Category filtering logic - ensure both main category and subcategory match
        if (filters?.main_category) {
          query = query.eq('category_main', filters.main_category);
        }

        // Then apply subcategory filter if present
        if (filters?.category_subcategory) {
          // For the top category buttons, we want to match any subcategory that contains this type
          // e.g., if category_subcategory is 'shoes', match 'mens-shoes', 'womens-shoes', 'kids-shoes'
          query = query.ilike('category_subcategory', `%${filters.category_subcategory}%`);
        }

        // Finally apply item filter if present
        if (filters?.category_item) {
          query = query.eq('category_item', filters.category_item);
        }

        // Apply condition filter
        if (filters?.condition) {
          query = query.eq('condition', filters.condition);
        }

        // Apply size filter
        if (filters?.size) {
          // Use the contains operator but with uppercase size value
          // (PostgreSQL array comparison is case-sensitive)
          const sizeValue = filters.size.toUpperCase(); // Convert to uppercase (XS, S, M, L, XL)
          query = query.contains('size', [sizeValue]);
        }

        // Apply price range filter
        if (filters?.priceRange) {
          const [min, max] = filters.priceRange.split('-');
          if (min && max) {
            query = query.gte('price', parseFloat(min)).lte('price', parseFloat(max));
          } else if (min) {
            // Handle cases like "100+" where there's no max
            query = query.gte('price', parseFloat(min));
          }
        }

        // Apply rating filter
        if (filters?.rating) {
          const minRating = parseInt(filters.rating);
          query = query.gte('vendor.rating', minRating);
        }

        // Filter out inactive vendors
        query = query.eq('vendor.status', 'active');

        // Filter out inactive products - only show active products with quantity > 0
        query = query.eq('status', 'active').gt('quantity', 0);

        // Apply sorting
        if (sortBy === 'price_asc') {
          query = query.order('price', { ascending: true });
        } else if (sortBy === 'price_desc') {
          query = query.order('price', { ascending: false });
        } else {
          query = query.order('created_at', { ascending: false });
        }

        // Apply limit if specified
        if (limit) {
          query = query.limit(limit);
        }

        const { data, error } = await query;

        if (error) {
          logger.error('useProducts', 'Error fetching products', error);
          throw error;
        }

        let products = data || [];

        // Check for vendor address changes and invalidate cache if needed
        try {
          const vendorsWithChanges = await vendorAddressTracker.processProducts(products);
          
          if (vendorsWithChanges.length > 0) {
            logger.info('useProducts', `Address changes detected for ${vendorsWithChanges.length} vendors`, vendorsWithChanges);
            
            // Get cache stats before clearing
            const statsBefore = deliveryServiceabilityService.getCacheStats();
            
            // Clear vendor-specific cache entries
            await deliveryServiceabilityService.clearVendorCache(vendorsWithChanges);
            
            // Get cache stats after clearing
            const statsAfter = deliveryServiceabilityService.getCacheStats();
            
            logger.debug('useProducts', 'Cache invalidation complete', {
              vendorsAffected: vendorsWithChanges.length,
              entriesCleared: statsBefore.totalEntries - statsAfter.totalEntries,
              remainingEntries: statsAfter.totalEntries
            });
          } else {
            // Log registry stats occasionally for monitoring
            const addressStats = vendorAddressTracker.getStats();
            logger.debug('useProducts', `Address registry: ${addressStats.totalVendors} vendors tracked, ${addressStats.withCoordinates} with coordinates`);
          }
        } catch (error) {
          logger.warn('useProducts', 'Failed to process vendor address changes', error);
          // Continue with normal flow even if address tracking fails
        }

        // Always apply delivery range filtering if user has a delivery address
        if (selectedAddress) {
          const userHasValidCoordinates = GeocodingService.areCoordinatesValid(
            selectedAddress.latitude, 
            selectedAddress.longitude
          );

          logger.debug('useProducts', 'User coordinates valid', { valid: userHasValidCoordinates, address: selectedAddress });

          if (userHasValidCoordinates) {
            // Use Uber Direct serviceability check with caching
            const serviceabilityPromises = products.map(async (product) => {
              const vendor = product.vendor;
              
              // Skip filtering if vendor doesn't have coordinates
              const vendorHasValidCoordinates = GeocodingService.areCoordinatesValid(
                vendor?.latitude, 
                vendor?.longitude
              );

              if (!vendorHasValidCoordinates) {
                logger.warn('useProducts', `Vendor ${vendor?.store_name} missing coordinates - excluding from results`);
                return { product, isServiceable: false };
              }

              try {
                // Check Uber Direct serviceability with caching
                const serviceabilityResult = await deliveryServiceabilityService.checkServiceability({
                  vendorId: vendor.id,
                  vendorLatitude: vendor.latitude!,
                  vendorLongitude: vendor.longitude!,
                  userLatitude: selectedAddress.latitude!,
                  userLongitude: selectedAddress.longitude!,
                  vendorAddress: {
                    street_address: [vendor.street_line1 || 'Business Address'],
                    city: vendor.city || 'New York',
                    state: vendor.state || 'NY',
                    zip_code: vendor.zip_code || '10001',
                    country: vendor.country || 'US'
                  },
                  userAddress: {
                    street_address: [selectedAddress.street_address || 'Customer Address'],
                    city: selectedAddress.city || 'New York',
                    state: selectedAddress.state || 'NY', 
                    zip_code: selectedAddress.zip_code || '10001',
                    country: 'US'
                  }
                });

                const logPrefix = serviceabilityResult.fallbackUsed ? 'PRODUCTS-FALLBACK' : 'PRODUCTS-UBER';
                logger.debug(logPrefix, `Vendor ${vendor.store_name}: ${serviceabilityResult.isServiceable ? 'Serviceable' : 'Not serviceable'}`);

                return { product, isServiceable: serviceabilityResult.isServiceable };
              } catch (error) {
                logger.error('useProducts', `Serviceability check failed for vendor ${vendor.store_name}`, error);
                // Fallback to distance-based check on error
                const distance = calculateDistance(
                  selectedAddress.latitude!,
                  selectedAddress.longitude!,
                  vendor.latitude!,
                  vendor.longitude!
                );
                const isServiceable = distance <= 8; // Conservative 8-mile fallback
                logger.debug('useProducts', `Error fallback for ${vendor.store_name}: distance ${distance.toFixed(2)} miles, serviceable: ${isServiceable}`);
                return { product, isServiceable };
              }
            });

            // Wait for all serviceability checks to complete
            const serviceabilityResults = await Promise.all(serviceabilityPromises);
            
            // Filter products based on serviceability
            products = serviceabilityResults
              .filter(result => result.isServiceable)
              .map(result => result.product);

            logger.debug('useProducts', `Filtered ${serviceabilityResults.length} products to ${products.length} serviceable products`);
          } else {
            logger.warn('useProducts', 'User delivery address missing coordinates - returning empty results');
            // Return empty array instead of showing all products when coordinates are invalid
            products = [];
          }
        }

        // Apply availability filter (open/closed vendors) client-side
        if (filters?.availability) {
          const availabilityFilter = filters.availability;
          products = products.filter((product) => {
            const vendor = product.vendor;
            if (!vendor) return false;
            
            const storeStatus = isStoreCurrentlyOpen(
              vendor.delivery_hours,
              { city: vendor.city, state: vendor.state, country: vendor.country }
            );
            
            if (availabilityFilter === 'open') {
              return storeStatus.isOpen;
            } else if (availabilityFilter === 'closed') {
              return !storeStatus.isOpen;
            }
            return true;
          });
          
          logger.debug('useProducts', `After availability filter (${availabilityFilter}): ${products.length} products`);
        }

        logger.debug('useProducts', `Returning ${products.length} products with updated quantities`);
        if (products.length > 0) {
          logger.debug('useProducts', 'Sample product quantities', products.slice(0, 3).map(p => `${p.title}: ${p.quantity}`));
        }
        
        return products;
      } catch (error) {
        logger.error('useProducts', 'Error in query', error);
        return [];
      }
    },
    // Add staleTime and cacheTime to improve performance
    staleTime: 1000 * 30,     // 30 seconds before refetching
    gcTime: 1000 * 60 * 10,   // 10 minutes before clearing cache
    // Avoid refetching on window focus which can cause flickering
    refetchOnWindowFocus: false,
  });
} 